import type { Resource } from '@medplum/fhirtypes';
import { type ReactNode, createContext, useContext } from 'react';

import type { ResourceOfType, ResourceTypeName } from './view-contract';

/**
 * Candidate resources an edit variant may offer for a Reference field,
 * keyed by the referenced resourceType.
 *
 * This exists because of the renderer's data boundary: a picker needs a
 * roster (every Location in the org), but a view may not fetch one. The
 * consuming feature lib does the fetching and hands the roster down; the
 * view stays presentational and testable with a plain array.
 *
 * Absent by design rather than by accident: a view that finds no roster for
 * its resourceType renders no picker, so surfaces that never provide one
 * (stories, print, chat) degrade to read-only rather than to an empty select.
 * A provided-but-empty roster is a different state — the picker renders and
 * says so.
 */
export type FhirReferenceOptions = Partial<
  Record<ResourceTypeName, readonly Resource[]>
>;

const ReferenceOptionsContext = createContext<FhirReferenceOptions>({});

export function FhirReferenceOptionsProvider({
  options,
  children,
}: {
  options: FhirReferenceOptions;
  children: ReactNode;
}) {
  return (
    <ReferenceOptionsContext.Provider value={options}>
      {children}
    </ReferenceOptionsContext.Provider>
  );
}

/**
 * The offerable resources of one type, or undefined when no roster was
 * provided. Callers treat undefined as "no picker" and [] as "no matches".
 */
export function useFhirReferenceOptions<K extends ResourceTypeName>(
  resourceType: K,
): readonly ResourceOfType<K>[] | undefined {
  const options = useContext(ReferenceOptionsContext);
  return options[resourceType] as readonly ResourceOfType<K>[] | undefined;
}
